<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
  >
    <div :class="$style.list">
      <div :class="$style.alerts">
        <BaseAlert
          v-if="totalTwPayments > 0"
          color="warning"
          :text="
            $t('register.tw_payment_info', {
              amount: filters.currency(totalTwPayments)
            })
          "
          v-test="'register-order-tw-payment-alert'"
        />
        <BaseAlert
          v-if="serviceItemMismatch"
          color="info"
          :text="$t('register.service_item_mismatch')"
          v-test="'register-order-service-item-mismatch'"
        />
        <AppointmentDiscountCode
          v-for="(discountCode, index) in discountCodes"
          :key="index"
          :discountCode="discountCode"
          v-test="'register-order-discount-alert'"
        />
      </div>

      <div
        v-if="orderAppointments.length"
        :class="$style.appointments"
      >
        <OrderAppointment
          v-for="(appointment, index) in orderAppointments"
          :key="index"
          :appointment="appointment"
        />
      </div>

      <div :class="$style.items">
        <TransitionGroup
          name="list-pop-slide"
          :appear="order.items.length === 1"
        >
          <template
            v-for="row in packageRows"
            :key="rowKey(row)"
          >
            <OrderServicePackage
              v-if="row.kind === 'package'"
              :servicePackage="row.servicePackage"
              :indexes="row.indexes"
            />
            <OrderItem
              v-else
              :item="order.items[row.indexes[0]]"
              :index="row.indexes[0]"
            />
          </template>
        </TransitionGroup>
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import AppointmentDiscountCode from '@/components/AppointmentDiscountCode.vue';
import filters from '@/filters';
import {
  groupPackageRows,
  type PackageRow,
} from '@/helpers/service-package-rows';
import type { OrderItem as OrderItemType } from '@/modules/register/stores/order';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import { useServicesStore } from '@/stores/services';
import unleash from '@/unleash';
import OrderAppointment from './OrderAppointment.vue';
import OrderItem from './OrderItem.vue';
import OrderServicePackage from './OrderServicePackage.vue';

defineOptions({
  name: 'OrderItems',
});

const { order } = useRegisterOrderStore();
const { orderAppointments, totalTwPayments } = storeToRefs(
  useRegisterOrderStore(),
);
const { servicePackageByServicePackageServiceId } = useServicesStore();

const servicePackagesEnabled = unleash.isEnabled('ServicePackages');

const packageRows = computed(() =>
  groupPackageRows(order.items, (item) =>
    servicePackagesEnabled
      ? servicePackageByServicePackageServiceId(item.servicePackageServiceId)
      : undefined,
  ),
);

const rowKey = (row: PackageRow) => {
  const firstItem = order.items[row.indexes[0]];
  return row.kind === 'package'
    ? `package-${firstItem._uid}`
    : `${firstItem._uid}-${firstItem.name}`;
};

const discountCodes = computed(() =>
  orderAppointments.value
    .map((appointment) => appointment.discountCode)
    .filter((value, index, self) => value && self.indexOf(value) === index),
);

const serviceItemMismatch = computed(() => {
  // for online appointments we create an order with the appointment services and send this to the customer.
  // If the user then changed the appointment's services,
  // we should show a warning that the order's services don't match the original order's services.

  if (order.originalOrderId || !orderAppointments.value.length) {
    // no mismatch if crediting order or if order doesnt have appointments
    return false;
  }

  const orderPaidOnline =
    !!order.transactions.find(
      (transaction) =>
        transaction.type === 'ONLINE' ||
        transaction.type === 'TREATWELL_ONLINE_PAYMENT',
    ) || !!order.items.find((item) => item.transactionCost);

  if (!orderPaidOnline) {
    // no mismatch if order was not paid online
    return false;
  }

  let mismatch = false;
  orderAppointments.value.forEach((appointment) => {
    const appointmentItems = order.items.filter(
      (item: OrderItemType) => item.appointmentId === appointment.id,
    );
    if (!appointmentItems.length) {
      // mismatch if the order doesnt show the appointments services at all
      mismatch = true;
      return;
    } else {
      appointment.serviceIds?.forEach((serviceId) => {
        if (
          !appointmentItems
            .flatMap((item) => item.serviceId)
            .includes(serviceId)
        ) {
          // mismatch if the appointment services don't match the order services
          mismatch = true;
          return;
        }
      });
    }
  });

  return mismatch;
});
</script>

<style
  lang="scss"
  module
>
.list {
  position: relative;
}

.appointments {
  margin-bottom: $spacing;
}

.items {
  & > *:not(:last-child) {
    margin-bottom: $spacing * 0.5;
  }
}

.appointments {
  & > *:not(:last-child) {
    margin-bottom: $spacing * 0.5;
  }
}

.alerts {
  & > * {
    margin-bottom: $spacing * 0.5;
  }
}
</style>

<style
  lang="scss"
  scoped
>
.list-pop-slide-move {
  &:last-child {
    margin-bottom: 80px; // Add a margin to the bottom to make prevent the overflow on the scroll container from cutting off the content during the transition
  }
}
</style>
